/**
 * Form Builder Types
 *
 * Type definitions for the drag-and-drop form builder system.
 * These types define the structure of forms, fields, validation rules,
 * and various configuration options.
 */

// ============================================================================
// Field Types
// ============================================================================

/**
 * All supported field types in the form builder
 */
export type FieldType =
  // Basic input types
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'textarea'
  // Selection types
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'switch'
  | 'radio'
  // Date/Time types
  | 'date'
  | 'datetime'
  | 'time'
  // Media types
  | 'file'
  | 'image'
  // Advanced types
  | 'slider'
  | 'rating'
  | 'color'
  | 'richtext'
  // Layout elements (non-input)
  | 'heading'
  | 'paragraph'
  | 'divider'
  | 'spacer';

/**
 * Option for select, multiselect, radio, and checkbox group fields
 */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

// ============================================================================
// Validation
// ============================================================================

/**
 * Types of validation rules available
 */
export type ValidationRuleType =
  | 'required'
  | 'minLength'
  | 'maxLength'
  | 'min'
  | 'max'
  | 'pattern'
  | 'email'
  | 'url'
  | 'custom';

/**
 * A validation rule for a form field
 */
export interface ValidationRule {
  /** Type of validation */
  type: ValidationRuleType;
  /** Value for the rule (e.g., min length number, regex pattern string) */
  value?: string | number | RegExp;
  /** Error message to display when validation fails */
  message: string;
  /** Localization key for the error message */
  messageKey?: string;
}

// ============================================================================
// Conditional Logic
// ============================================================================

/**
 * Operators for conditional field visibility
 */
export type ConditionalOperator = 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'notContains' | 'isEmpty' | 'isNotEmpty';

/**
 * Condition for showing/hiding a field based on another field's value
 */
export interface FieldCondition {
  /** Name of the field to check */
  field: string;
  /** Comparison operator */
  operator: ConditionalOperator;
  /** Value to compare against */
  value?: unknown;
}

// ============================================================================
// Field Definition
// ============================================================================

/**
 * Complete definition of a form field in the builder
 */
export interface FormFieldDefinition {
  /** Unique instance ID for this field */
  id: string;
  /** Type of field */
  type: FieldType;
  /** Field name used in form data (must be unique within form) */
  name: string;
  /** Display label for the field */
  label: string;
  /** Localization key for the label */
  labelKey?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Localization key for placeholder */
  placeholderKey?: string;
  /** Description text shown below the field */
  description?: string;
  /** Localization key for description */
  descriptionKey?: string;
  /** Help text (additional guidance) */
  helpText?: string;
  /** Localization key for help text */
  helpTextKey?: string;
  /** Default value for the field */
  defaultValue?: unknown;

  // Layout
  /** Number of grid columns this field spans (1-4) */
  colSpan?: 1 | 2 | 3 | 4;

  // Validation
  /** Whether the field is required */
  required?: boolean;
  /** Validation rules for the field */
  validation?: ValidationRule[];

  // Field-specific options
  /** Options for select, multiselect, radio, checkbox group */
  options?: SelectOption[];
  /** Minimum value (for number, slider, rating) */
  min?: number;
  /** Maximum value (for number, slider, rating) */
  max?: number;
  /** Step increment (for number, slider) */
  step?: number;
  /** Accepted file types (for file, image) */
  accept?: string;
  /** Allow multiple file selection */
  multiple?: boolean;
  /** Number of rows (for textarea) */
  rows?: number;

  // Layout element specific
  /** Heading level for heading type (1-6) */
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Content for paragraph type */
  content?: string;
  /** Height for spacer type */
  spacerHeight?: number;

  // Conditional visibility
  /** Conditions that must be met for this field to be visible */
  showWhen?: FieldCondition[];
  /** Logic operator for multiple conditions */
  showWhenLogic?: 'and' | 'or';

  // Styling
  /** Custom CSS class name */
  className?: string;

  // State
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is read-only */
  readOnly?: boolean;
}

// ============================================================================
// Form Settings
// ============================================================================

/**
 * Global form layout settings
 */
export interface FormSettings {
  /** Form layout direction */
  layout: 'vertical' | 'horizontal' | 'inline';
  /** Number of columns in the form grid */
  columns: 1 | 2 | 3 | 4;
  /** Gap between form fields */
  gap: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Submit button text */
  submitButtonText: string;
  /** Submit button position */
  submitButtonPosition: 'left' | 'center' | 'right' | 'stretch';
  /** Whether to show a reset button */
  showResetButton: boolean;
  /** Reset button text */
  resetButtonText: string;
  /** Whether to validate on blur */
  validateOnBlur: boolean;
  /** Whether to validate on change */
  validateOnChange: boolean;
  /** Gradient variant for buttons */
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  /** Compact mode for smaller fields */
  compact?: boolean;
}

/**
 * Default form settings
 */
export const DEFAULT_FORM_SETTINGS: FormSettings = {
  layout: 'vertical',
  columns: 1,
  gap: 'md',
  submitButtonText: 'Submit',
  submitButtonPosition: 'right',
  showResetButton: false,
  resetButtonText: 'Reset',
  validateOnBlur: true,
  validateOnChange: false,
  gradientVariant: 'primary',
  compact: false,
};

// ============================================================================
// Form Definition
// ============================================================================

/**
 * Complete form definition that can be saved/loaded
 */
export interface FormDefinition {
  /** Unique form ID */
  id: string;
  /** Form name/title */
  name: string;
  /** Form description */
  description?: string;
  /** Fields in the form */
  fields: FormFieldDefinition[];
  /** Form settings */
  settings: FormSettings;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string;
  /** Version number for tracking changes */
  version?: number;
}

// ============================================================================
// Form Builder State
// ============================================================================

/**
 * Drag operation types
 */
export type DragOperation = 'add' | 'reorder';

/**
 * Field palette category
 */
export interface FieldCategory {
  id: string;
  label: string;
  labelKey?: string;
  fields: FieldType[];
}

/**
 * Clipboard data for copy/paste operations
 */
export interface ClipboardData {
  type: 'field' | 'fields';
  data: FormFieldDefinition | FormFieldDefinition[];
  timestamp: number;
}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Props for the main FormBuilder component
 */
export interface FormBuilderProps {
  /** Initial form definition to load */
  initialForm?: FormDefinition;
  /** Callback when form is saved */
  onSave?: (form: FormDefinition) => void;
  /** Callback when form builder is cancelled */
  onCancel?: () => void;
  /** Whether the form builder is in read-only mode */
  readOnly?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Props for the FormBuilderCanvas component
 */
export interface FormBuilderCanvasProps {
  /** Custom class name */
  className?: string;
}

/**
 * Props for the FormBuilderSidebar component
 */
export interface FormBuilderSidebarProps {
  /** Custom class name */
  className?: string;
}

/**
 * Props for the FormBuilderToolbar component
 */
export interface FormBuilderToolbarProps {
  /** Callback when save is clicked */
  onSave?: () => void;
  /** Callback when cancel is clicked */
  onCancel?: () => void;
  /** Custom class name */
  className?: string;
}

/**
 * Props for the FieldRenderer component
 */
export interface FieldRendererProps {
  /** Field definition to render */
  field: FormFieldDefinition;
  /** Index in the fields array */
  index: number;
  /** Whether the field is selected */
  isSelected: boolean;
  /** Whether the form is in edit mode */
  isEditMode: boolean;
  /** Callback when field is clicked */
  onClick?: () => void;
  /** Custom class name */
  className?: string;
}

/**
 * Props for the FieldSettingsPanel component
 */
export interface FieldSettingsPanelProps {
  /** Field to edit */
  field: FormFieldDefinition;
  /** Callback when field is updated */
  onUpdate: (updates: Partial<FormFieldDefinition>) => void;
  /** Callback when panel is closed */
  onClose: () => void;
}

/**
 * Props for the FormSettingsPanel component
 */
export interface FormSettingsPanelProps {
  /** Current form settings */
  settings: FormSettings;
  /** Callback when settings are updated */
  onUpdate: (updates: Partial<FormSettings>) => void;
  /** Callback when panel is closed */
  onClose: () => void;
}

/**
 * Props for the FormBuilderPreview component
 */
export interface FormBuilderPreviewProps {
  /** Custom class name */
  className?: string;
}
